//V1.0.0
//Instance of a debugbase app
//Creates instances of the main helpers and
/**
 * App core (formerly debugbase/App.ts). Owns the managers and
 * THE TICK PIPELINE — animation-feel-critical, do not reorder:
 *   window.deltaTime accumulates data.delta.deltaTime each frame,
 *   ParticleBehaviourLookup.pingPongMode doubles it,
 *   it is clamped at 1.99 then 1.8,
 *   then per triggered tick: preTicks -> DelayHelper.tick ->
 *   SiteTransitions.tick -> modules -> controllers, then deltaTime = 0.
 */
import { ControllerManager } from 'core/ControllerManager';
import { RouteManager } from 'core/router';
import { ModuleManager } from 'core/ModuleManager';

import { DOMHelper } from 'core/DOMHelper';
import { FrameworkHelper } from 'core/FrameworkHelper';

import { Events } from 'core/events/Events';

import DelayHelper from 'utils/timers';
import { device } from 'utils/device';
import ParticleBehaviourLookup from 'particles/ParticleBehaviourLookup';
import SiteTransitions from 'app/services/SiteTransitions';

export class App {
	private _controllerManager: ControllerManager;
	private _routeManager: RouteManager;
	private _moduleManager: ModuleManager;

	private _domHelper: DOMHelper;
	private _frameworkHelper: FrameworkHelper;

	public static instance: App;

	private _preTicks: Array<{ callback: CallableFunction; id: string; tag: string }> = [];

	private stats: any;
	count: number = 0;

	private _accumulatorTime = 0;

	constructor() {
		//device took its viewport snapshot at module init (utils/device), so it
		//already holds the current window size here — no boot write needed:
		const doc = document.documentElement;
		doc.style.setProperty('--doc-height', `${device.innerHeight}px`);

		Events.init();
		this._createManagers();
		this._addListeners();
		App.instance = this;

		if (!this.stats) {
		}
	}

	public init(): void {
		this._controllerManager.init();
		this._routeManager.init();
	}

	public get controller(): ControllerManager {
		return this._controllerManager;
	}

	public get route(): RouteManager {
		return this._routeManager;
	}

	public get module(): ModuleManager {
		return this._moduleManager;
	}

	public get DOM(): DOMHelper {
		return this._domHelper;
	}

	public get framework(): FrameworkHelper {
		return this._frameworkHelper;
	}

	public addPreTick(callback: CallableFunction, id: string = null, tag: string = null): void {
		this._preTicks.push({
			callback: callback,
			id: id,
			tag: tag
		});
	}

	public removePreTickById(id: string = null): void {
		console.log('this._preTicks remove start', this._preTicks.length);

		for (let i = 0; i < this._preTicks.length; i++) {
			if (this._preTicks[i].id == id) {
				this._preTicks.splice(i, 1);
				console.log('this._preTicks remove end', this._preTicks.length);
				return;
			}
		}
	}

	private _createManagers(): void {
		this._controllerManager = new ControllerManager();
		this._routeManager = new RouteManager();
		this._routeManager.controllerManager = this._controllerManager;
		this._moduleManager = new ModuleManager();

		this._domHelper = new DOMHelper();
		this._frameworkHelper = new FrameworkHelper();
	}

	//Add high level listeners:
	private _addListeners(): void {
		Events.ready(this._onDOMReady.bind(this));
		Events.tick(this._onTick.bind(this));
		Events.DOMUpdate(this._onDOMUpdate.bind(this));
		Events.scroll(this._onScroll.bind(this));
		Events.resize(this._onResize.bind(this));
	}

	//Heartbeat ticker:

	private _onTick(data): void {
		let useDeltaTime = true;
		let triggerAtDelta = 0.9;
		let useManualTrigger = true;
		let triggerAtMs = 16;

		//Manual trigger no delat (each frame x1). Approx FPS 60

		//Unbound FPS. Tick on every frame. Delta time managed. Varaiable rate.
		useDeltaTime = true;
		useManualTrigger = false;
		triggerAtDelta = 0;

		if (!window.deltaTime) {
			window.deltaTime = 0;
		}

		if (useDeltaTime) window.deltaTime += data.delta.deltaTime;
		else window.deltaTime = 1;

		if (ParticleBehaviourLookup.pingPongMode) {
			window.deltaTime *= 2;
		}

		let tigger = false;

		if (useManualTrigger) {
			this._accumulatorTime += data.deltaMs;
			if (this._accumulatorTime >= triggerAtMs) {
				this._accumulatorTime -= triggerAtMs;
				tigger = true;
			}
		} else {
			tigger = window.deltaTime >= triggerAtDelta;
		}

		if ((window as any).appIsPaused) {
			return;
		}

		if (window.deltaTime > 1.99) {
			window.deltaTime = 1.99;
		}

		if (window.deltaTime > 1.8) {
			window.deltaTime = 1.8;
		}

		if (tigger) {
			if (this.stats) {
				this.stats.begin();
			}

			this._doPreTicks(1);
			DelayHelper.tick();
			SiteTransitions.tick();
			//(ModuleManager.tick takes no delta — a stray `1` used to be passed
			//and silently dropped; module ticks are deltaless by design.)
			this._moduleManager.tick();
			this._controllerManager.tick(1);

			window.deltaTime = 0;

			if (this.stats) {
				this.stats.end();
			}
		}
	}

	private _onDOMReady(): void {
		this._controllerManager.DOMReady();
		this._moduleManager.DOMReady();
	}

	private _onDOMUpdate(): void {
		this._controllerManager.DOMUpdated();
		this._moduleManager.DOMUpdated();
	}

	private _onScroll(y: number): void {
		this._domHelper.scroll(y);
		this._controllerManager.scroll(y);
		this._moduleManager.scroll(y);
	}

	private _onResize(sizes): void {
		//device is THE viewport lookup (ex window.cachedInnerWidth/Height) and
		//its snapshot MUST refresh before anything else in the resize cascade —
		//the controller/module fan-out below reads device.innerWidth/Height:
		device.resize();

		const doc = document.documentElement;
		doc.style.setProperty('--doc-height', `${device.innerHeight}px`);

		this._domHelper.resize(sizes);
		this._controllerManager.resize(sizes);
		this._moduleManager.resize(sizes);
	}

	private _doPreTicks(delta: number): void {
		for (let i = 0; i < this._preTicks.length; i++) {
			this._preTicks[i].callback(delta);
		}
	}
}
